import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Checkbox, Divider, Empty, Select, Space, Spin, Table, Tabs, Typography } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import moment from "moment";
import { getPatientAppointmentHistory, getPatientCareCredits } from "../../../redux/doctorSlice";
import {
  CREDIT_BEARING_CATEGORIES,
  CREDIT_TYPE_CODE_TO_KEY,
  PROVIDER_CATEGORIES,
  categoryForGroupName,
} from "../../../../utils/providerCategories";
import Header from "../Components/Header";

const { Title, Text } = Typography;

const sectionHeading = { display: "block", fontWeight: 600, letterSpacing: 0.5, marginBottom: 16 };

const FilterSelect = ({ placeholder, options, value, onChange }) => {
  const allSelected = options.length > 0 && value.length === options.length;
  return (
    <Select
      mode="multiple"
      allowClear
      placeholder={placeholder}
      maxTagCount="responsive"
      style={{ width: 240 }}
      options={options}
      value={value}
      onChange={onChange}
      dropdownRender={(menu) => (
        <>
          <div style={{ padding: "4px 12px" }}>
            <Checkbox
              checked={allSelected}
              indeterminate={value.length > 0 && !allSelected}
              onChange={(e) => onChange(e.target.checked ? options.map((o) => o.value) : [])}
            >
              Select All
            </Checkbox>
          </div>
          <Divider style={{ margin: "4px 0" }} />
          {menu}
        </>
      )}
    />
  );
};

const CareHistory = () => {
  const dispatch = useDispatch();
  const patient = JSON.parse(localStorage.getItem("patient") || "{}");
  const patientRef = patient?.userRef;

  const [credits, setCredits] = useState(null);
  const [appointments, setAppointments] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [providerFilter, setProviderFilter] = useState([]);
  const [roleFilter, setRoleFilter] = useState([]);

  const fetchCredits = useCallback(async () => {
    if (!patientRef) return;
    setLoadingCredits(true);
    const result = await dispatch(getPatientCareCredits(patientRef));
    setCredits(getPatientCareCredits.fulfilled.match(result) ? result.payload : null);
    setLoadingCredits(false);
  }, [dispatch, patientRef]);

  const fetchAppointments = useCallback(async () => {
    if (!patientRef) return;
    setLoadingAppointments(true);
    const result = await dispatch(getPatientAppointmentHistory(patientRef));
    setAppointments(getPatientAppointmentHistory.fulfilled.match(result) ? result.payload : null);
    setLoadingAppointments(false);
  }, [dispatch, patientRef]);

  useEffect(() => {
    fetchCredits();
    fetchAppointments();
  }, [fetchCredits, fetchAppointments]);

  const creditRows = useMemo(() => {
    if (!Array.isArray(credits)) return [];
    return CREDIT_BEARING_CATEGORIES.map((category) => {
      const row = credits.find((c) => CREDIT_TYPE_CODE_TO_KEY[c.code] === category.creditKey);
      return row ? { label: category.label, remaining: row.remaining ?? 0, total: row.total ?? 0 } : null;
    }).filter(Boolean);
  }, [credits]);

  const appointmentRows = useMemo(
    () =>
      (Array.isArray(appointments) ? appointments : [])
        .filter((a) => a.status !== "Cancelled")
        .map((a) => ({
          ...a,
          category: a.category || categoryForGroupName(a.roleName)?.label || "",
        }))
        .sort((a, b) => moment(b.appointmentDate).valueOf() - moment(a.appointmentDate).valueOf()),
    [appointments],
  );

  const providerOptions = useMemo(
    () =>
      [...new Set(appointmentRows.map((a) => a.providerName).filter(Boolean))]
        .sort()
        .map((name) => ({ label: name, value: name })),
    [appointmentRows],
  );

  const roleOptions = PROVIDER_CATEGORIES.map((c) => ({ label: c.label, value: c.label }));

  const filteredAppointments = appointmentRows.filter(
    (a) =>
      (!providerFilter.length || providerFilter.includes(a.providerName)) &&
      (!roleFilter.length || roleFilter.includes(a.category)),
  );

  const columns = [
    {
      title: "DATE",
      dataIndex: "appointmentDate",
      render: (date) => (
        <span style={{ color: "#8c8c8c", fontStyle: "italic" }}>
          {date ? moment(date).format("MMM D, YYYY") : "—"}
        </span>
      ),
    },
    {
      title: "PROVIDER",
      dataIndex: "providerName",
      render: (name, record) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Avatar
            size={28}
            src={record.providerProfilePhoto || undefined}
            {...(record.providerInitials ? { children: record.providerInitials } : { icon: <UserOutlined /> })}
          />
          {name || "—"}
        </span>
      ),
    },
    { title: "ROLE", dataIndex: "category", render: (v) => v || "—" },
    { title: "TITLE", dataIndex: "roleName", render: (v) => v || "—" },
  ];

  return (
    <div style={{ padding: 24 }}>
      {patientRef ? (
        <Header />
      ) : (
        <div style={{ marginBottom: 20 }}>Select a patient to view their care history.</div>
      )}

      <Title level={4} style={{ color: "#335CAD", letterSpacing: 0.5 }}>CARE HISTORY</Title>

      <Tabs
        defaultActiveKey="credits"
        items={[
          {
            key: "credits",
            label: "Outstanding Credits",
            children: (
              <>
                <Text style={sectionHeading}>OUTSTANDING APPOINTMENT CREDITS</Text>
                {loadingCredits ? (
                  <Spin />
                ) : creditRows.length ? (
                  <Space direction="vertical" size={8} style={{ paddingLeft: 16 }}>
                    {creditRows.map((row) => (
                      <div key={row.label} style={{ display: "flex", width: 240 }}>
                        <span style={{ flex: 1 }}>{row.label}</span>
                        <span>
                          <span style={{ color: "#52c41a", fontWeight: 600 }}>{row.remaining}</span> /{row.total}
                        </span>
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={credits ? "No appointment credits yet." : "Credit information isn't available yet."}
                  />
                )}
              </>
            ),
          },
          {
            key: "appointments",
            label: "Appointment Hx.",
            children: (
              <>
                <Text style={sectionHeading}>APPOINTMENT HISTORY</Text>
                <Space style={{ marginBottom: 16 }} wrap>
                  <FilterSelect
                    placeholder="Provider"
                    options={providerOptions}
                    value={providerFilter}
                    onChange={setProviderFilter}
                  />
                  <FilterSelect placeholder="Role" options={roleOptions} value={roleFilter} onChange={setRoleFilter} />
                </Space>
                <Table
                  rowKey="appointmentId"
                  size="small"
                  loading={loadingAppointments}
                  columns={columns}
                  dataSource={filteredAppointments}
                  pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          appointments ? "No appointments match." : "Appointment history isn't available yet."
                        }
                      />
                    ),
                  }}
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default CareHistory;
